import { useCallback, useEffect, useState } from 'react';
import { isDesktopShell } from '@/features/desktop/bridge';
import { notify } from '@/features/notifications/toastApi';
import { useOpenCall } from '../hooks/useOpenCall';
import { IncomingCallPopup } from './components/IncomingCallPopup';
import {
  useCallActions, useCallRingFeed, useIncomingCalls, useRingTimeout, useShouldRing,
} from './hooks/useIncomingCalls';
import { useEscalatedCall } from './hooks/useEscalatedCall';
import { useRingtone } from './hooks/useRingtone';
import { useTransferredCall } from './hooks/useTransferredCall';

/**
 * Renders the ringing stack. Only the head call rings and animates; older
 * invites collapse to a quiet, scaled-down card behind it.
 */
export function IncomingCallHost() {
  const calls = useIncomingCalls();
  const openCall = useOpenCall();
  const head = calls[0];
  /* While the quick-reply menu is open it owns Escape — closing it must not
     also hang up on the caller. */
  const [replyMenuOpen, setReplyMenuOpen] = useState(false);

  useCallRingFeed();
  /* An attended transfer hands you a call without ringing you; this is what
     moves you into its room when it does. */
  useTransferredCall();
  /* And "Move to meeting" hands you a MEETING without ringing you. Same place,
     same reason — and here rather than in the call surface because a meeting
     must never open in a desktop call window. */
  useEscalatedCall();
  useRingTimeout(calls.length > 0);
  useRingtone(useShouldRing(head), head?.ringtone);

  const { accept, decline, reply } = useCallActions(head?.callId);

  /**
   * ANSWERING OPENS `/call/:callId`, NOT `/meet/<sfuRoom>`.
   *
   * The old code navigated to the MEETING route with the SFU room name in the
   * meeting-id slot; `useMeetingDetail` then 404'd and every answered call
   * dead-ended on "This meeting is not available". A call has its own media
   * surface, and the grant travels to it through `activeCallStore`.
   */
  const onAccept = useCallback(() => {
    /* Read off the card BEFORE answering: accepting clears the ring, and the
       window still needs a name to put in its title bar. */
    const peerName = head?.caller.displayName ?? '';
    const kind = head?.kind ?? 'audio';
    void accept(kind === 'video').then((callId) => {
      if (callId === null) {
        notify.error('Could not join the call. Try again.');
        return;
      }
      /* On the desktop the call takes its own window, so the workspace
         survives behind it: the conversation you were reading, the chat list
         and everybody's presence stay exactly where they were. In a browser
         there is no window to take — a second one would boot with no token and
         rotate the shared refresh cookie — so it opens in this document and
         shrinks to a corner tile when you navigate away. */
      openCall({ callId, peerName, kind, startsConnected: true });
    });
  }, [accept, head?.kind, head?.caller.displayName, openCall]);

  const onDecline = useCallback(() => {
    void decline();
  }, [decline]);

  const onReply = useCallback(
    (message: string) => {
      void reply(message).then(() => notify.success('Reply sent.'));
    },
    [reply],
  );

  /*
   * ON THE DESKTOP THE RINGING CARD IS A REAL WINDOW, NOT THIS.
   *
   * `useDesktopCalls` mirrors every invite onto the shell's always-on-top
   * ringer — 380x216, genuine Answer and Decline, over full-screen apps, alive
   * while the workspace is hidden in the tray. This card drawn as well was the
   * "calls coming seperately in popups" half of the complaint: two ringing
   * cards for one call, one of them inside a window the user was not looking
   * at.
   *
   * ONLY THE RENDER IS SUPPRESSED. Every hook above still runs, and they are
   * the load-bearing half: the ring feed, the ringtone (the shell's ringer
   * window has no audio of its own), the ring timeout, and the transfer and
   * escalation handlers that move you into a room without ringing at all.
   */
  const nativeRinger = isDesktopShell();

  /* Escape declines the ringing call — a phone should always be dismissible.
     Not when the card is invisible, though: a keystroke in the workspace must
     never hang up on a caller whose ringer is a separate window. */
  useEffect(() => {
    if (!head || replyMenuOpen || nativeRinger) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onDecline();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [head, replyMenuOpen, nativeRinger, onDecline]);

  if (!head || nativeRinger) return null;

  return (
    <div className="flex flex-col items-end gap-2">
      <IncomingCallPopup
        call={head}
        onAccept={onAccept}
        onDecline={onDecline}
        onReply={onReply}
        onQuickReplyOpenChange={setReplyMenuOpen}
        ringing
      />

      {calls.length > 1 ? (
        <p className="rounded-pill bg-popup-raised px-3 py-1 text-sm font-medium text-popup-muted">
          {calls.length - 1} more incoming {calls.length === 2 ? 'call' : 'calls'}
        </p>
      ) : null}
    </div>
  );
}
